import type { Ref } from 'react';
import { forwardRef, memo } from 'react';
import { clsx } from 'clsx';
import { TextBlock } from '@library';
import ChevronLeftIcon from '../../../core/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../core/components/icons/ChevronRightIcon';
import useLoginState from '../../../core/hooks/useLoginState';
import Button from '../../../core/components/Button';
import useCarouselActions from '../../../core/hooks/useCarouselActions';
import combineAttributes from '../../util/combineAttributes';
import Carousel from '../../../core/components/Carousel';
import ProductStockIndicator from '../../../availability/components/ProductStockIndicator';
import type { ProductVariant } from '../../types';
import styles from './ProductAttributeSelector.module.scss';

interface ProductAttributeSelectorProps {
    grayBg?: boolean;
    notCentered?: boolean;
    variants: Pick<ProductVariant, 'id' | 'sku' | 'variationAttributes'>[];
    onAttributeSelected: (sku: string) => void;
    selectedVariantSKU?: string;
    mobile?: boolean;
    skipLoadingStocks?: boolean;
}

// eslint-disable-next-line @hostettler/memoize-component
const ProductAttributeSelectorItemForRef = forwardRef(
    (
        {
            sku,
            variationAttributes,
            mobile,
            selectedVariantSKU,
            grayBg,
            onVariantSelect,
            onKeyPress,
            isLoggedIn,
            skipLoadingStocks,
        }: {
            sku: string;
            variationAttributes: { name: string; value: string }[] | undefined;
            mobile: boolean | undefined;
            selectedVariantSKU: string | undefined;
            grayBg: boolean | undefined;
            onVariantSelect: (sku: string) => () => void;
            onKeyPress: (sku: string) => (e: React.KeyboardEvent) => void;
            isLoggedIn: boolean;
            skipLoadingStocks: boolean;
        },
        ref: Ref<HTMLDivElement>
    ) => {
        const text = combineAttributes(variationAttributes).value;

        const attributeText = mobile ? (
            <TextBlock tertiary regular lineHeightAsFontSize>
                {text}
            </TextBlock>
        ) : (
            <TextBlock caption regular uppercase center lineHeightAsFontSize>
                <span className={styles.attributeLabel}>{text}</span>
            </TextBlock>
        );
        return (
            <div
                ref={ref}
                key={sku}
                role="button"
                tabIndex={0}
                className={clsx(styles.variantContainer, {
                    [styles.selectedVariant]: sku === selectedVariantSKU,
                    [styles.grayBg]: grayBg,
                    [styles.mobile]: mobile,
                })}
                onClick={onVariantSelect(sku)}
                onKeyPress={onKeyPress(sku)}
                data-test-class="productTileVariantSelectorItem"
            >
                <div className={styles.content}>
                    {attributeText}
                    {isLoggedIn && (
                        <div
                            className={styles.productStockIndicator}
                            data-test-class="productStockIndicator"
                        >
                            <ProductStockIndicator
                                lineIndicator
                                sku={sku}
                                skipLoadingStocks={skipLoadingStocks}
                            />
                        </div>
                    )}
                    <div className={styles.selectEffect} />
                </div>
            </div>
        );
    }
);

const ProductAttributeSelectorItem = memo(ProductAttributeSelectorItemForRef);

const ProductAttributeSelector = ({
    grayBg = false,
    notCentered = false,
    variants,
    onAttributeSelected,
    selectedVariantSKU,
    mobile,
    skipLoadingStocks,
}: ProductAttributeSelectorProps) => {
    const isLoggedIn = useLoginState();
    const { nextItem, previousItem, controlsState, handleChangeActions } =
        useCarouselActions({ watchControlState: true });

    const onVariantSelect = (sku: string) => () => {
        if (sku !== selectedVariantSKU) {
            onAttributeSelected(sku);
        }
    };

    const onKeyPress = (sku: string) => (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
            onVariantSelect(sku)();
        }
    };

    return (
        <div
            className={clsx(styles.attributeCarousel, {
                [styles.centered]: !notCentered,
                [styles.mobile]: mobile,
            })}
        >
            {variants.length > 1 && controlsState.showControls && (
                <div className={styles.previousItemButton}>
                    <Button
                        iconButton
                        disabled={!controlsState.showLeft}
                        onPress={previousItem}
                    >
                        <ChevronLeftIcon
                            emphasized
                            iconLabel="carousel-previous-icon"
                            color="var(--default-icon-color-2)"
                        />
                    </Button>
                </div>
            )}
            <div className={clsx(styles.carouselBody)}>
                <Carousel
                    centered={!notCentered}
                    onChangeActions={handleChangeActions}
                >
                    {variants.map(({ sku, variationAttributes }) => {
                        return (
                            <ProductAttributeSelectorItem
                                key={sku}
                                sku={sku}
                                variationAttributes={variationAttributes}
                                mobile={mobile}
                                selectedVariantSKU={selectedVariantSKU}
                                grayBg={grayBg}
                                onVariantSelect={onVariantSelect}
                                onKeyPress={onKeyPress}
                                isLoggedIn={isLoggedIn}
                                skipLoadingStocks={!!skipLoadingStocks}
                            />
                        );
                    })}
                </Carousel>
                {controlsState.showLeft && (
                    <div className={styles.leftFadeEffect} />
                )}
                {controlsState.showRight && (
                    <div className={styles.rightFadeEffect} />
                )}
            </div>
            {variants.length > 1 && controlsState.showControls && (
                <div className={styles.nextItemButton}>
                    <Button
                        iconButton
                        disabled={!controlsState.showRight}
                        onPress={nextItem}
                    >
                        <ChevronRightIcon
                            emphasized
                            iconLabel="carousel-next-icon"
                            color="var(--default-icon-color-2)"
                        />
                    </Button>
                </div>
            )}
        </div>
    );
};

export default memo(ProductAttributeSelector);
